import React, { useRef, useState, useCallback } from 'react';
import PullToRefresh from 'react-simple-pull-to-refresh';

/**
 * Contexts
*/
import { useContext } from 'react';
import { AppContext } from '../../contexts/AppContext';

/**
 * MUI Components
 */
import { Box, CircularProgress } from '@mui/material';
import Fab from '@mui/material/Fab';
import SendIcon from '@mui/icons-material/Send';
import Dialog from '@mui/material/Dialog';
import { Snackbar, Alert } from '@mui/material';

/**
 * Components
 */
import ListNotifications from '../../components/Notifications/ListNotifications';
import SendNotificationForm from '../../components/Notifications/SendNotificationForm';

/**
 * Hooks
 */
import { useTranslation } from 'react-i18next';
import { useNetworkState } from '@uidotdev/usehooks';
import { useAppMenu } from '../../contexts/NavBarContext';
import useUserNotifications from '../../hooks/Notifications/useUserNotifications';

/**
 * Utils
 */
import { sendNotifications } from '../../utils/api';

const Notifications = () => {
    const { t } = useTranslation();
    const appData = useContext(AppContext);
    const networkState = useNetworkState();
    const [openForm, setOpenForm] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [toastOpen, setToastOpen] = useState(false);

    const { notifications, loading, error, loadMore, hasMore, forceRefresh } = useUserNotifications(appData.token);
    const { updateTitle } = useAppMenu();

    // Change the main menu title
    updateTitle(t('Notifications'));

    // Load more notifications when the user scrolls to the bottom of the list
    const observer = useRef();
    const lastItemRef = useCallback((node) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                loadMore();
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    // Notification dialog/form
    const handleOpenForm = () => setOpenForm(true);
    const handleCloseForm = () => setOpenForm(false);
    const handleSendNotification = (data) => {
        if (isSending) return;

        setIsSending(true);
        sendNotifications(appData.token, data).then(() => {
            setIsSending(false);
            setOpenForm(false);
            setToastOpen(true);
        }).catch((error) => {
            console.error('Error sending notification', error);
            setIsSending(false);
        });
    };

    return (
        <>
            <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="sm">
                <SendNotificationForm onClose={handleCloseForm} onSubmit={handleSendNotification} />
            </Dialog>
            <Box sx={{ p: 2, marginBottom: '100px' }}>
                <PullToRefresh onRefresh={forceRefresh}>
                    <ListNotifications items={notifications} lastItemRef={lastItemRef} />
                </PullToRefresh>
                {loading && (
                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                        <CircularProgress />
                    </Box>
                )}
                {!loading && !hasMore && notifications.length > 0 && (
                    <Box sx={{ textAlign: 'center', mt: 2, color: 'text.secondary' }}>
                        {t('No more notifications')}
                    </Box>
                )}
                {error && <div style={{ textAlign: 'center', color: 'red' }}>{t('error')}: {error.message}</div>}

                <Fab color="primary"
                    disabled={!networkState.online}
                    aria-label="send notification"
                    onClick={handleOpenForm}
                    sx={{ position: 'fixed', bottom: 80, right: 20 }}>
                    <SendIcon />
                </Fab>
            </Box>
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert onClose={() => setToastOpen(false)} severity="success" sx={{ width: '100%' }}>
                    {t('Notification sent successfully')}
                </Alert>
            </Snackbar>
        </>
    );
};

export default Notifications;